import { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import { AngleRight } from '@uikit/icons/AngleRight';
import { Menu } from '@uikit/icons/Menu';

const scrollToElement = (
  element: Element,
  { topOffset = 0, ...options }: { topOffset?: number } & Pick<ScrollToOptions, 'behavior'> = {},
) => {
  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.scrollY - topOffset;

  window.scrollTo({
    top: offsetPosition,
    ...options,
  });
};

const isElementInViewport = (element: Element): boolean => {
  const rect = element.getBoundingClientRect();

  return (
    rect.top >= -1 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

const TableOfContentLayout = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const [activeHeadingId, setActiveHeadingId] = useState<string | null>(null);
  const [isMenuHidden, setIsMenuHidden] = useState(true);
  const contentElRef = useRef<HTMLDivElement>(null);
  const [headings, setHeadings] = useState<{ id: string; title: string }[]>([]);

  useEffect(() => {
    const headingElements = Array.from(contentElRef.current?.querySelectorAll('h2') || []);

    const newHeadings = Array.from(headingElements || [], (headingElement) => {
      const title = headingElement.innerText;
      const id = title.toLowerCase().replace(/\s/g, '-');

      headingElement.id = id;

      return {
        id,
        title,
      };
    });

    setHeadings(newHeadings);

    const handleIntersectionObserver: IntersectionObserverCallback = (entries) => {
      const intersectedHeadings = new Set(
        entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target),
      );

      const currentSection = headingElements.find(
        (heading) => isElementInViewport(heading) || intersectedHeadings.has(heading),
      );

      if (currentSection) {
        setActiveHeadingId(currentSection.id);
      }
    };

    const observer = new IntersectionObserver(handleIntersectionObserver);

    headingElements.forEach((elem) => observer.observe(elem));

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    setActiveHeadingId(null);
  }, []);

  const scrollToHeading = (headingId: string) => {
    const anchor = `#${headingId}`;
    const element = document.querySelector(anchor);

    if (!element) {
      return;
    }

    scrollToElement(element, {
      topOffset: 190,
      behavior: 'smooth',
    });
  };

  const toggleMenu = () => {
    setIsMenuHidden((oldIsMenuHidden) => !oldIsMenuHidden);
  };

  return (
    <div className={classNames('flex gap-8 items-start relative', className)}>
      <div
        className={classNames(
          'w-[355px] flex flex-col gap-2.5 items-end max-w-full max-xl:p-4 max-xl:pt-20',
          'xl:sticky xl:top-[11.5rem]',
          'max-xl:fixed max-xl:h-full max-xl:justify-end max-xl:bottom-0 max-xl:right-0',
          {
            'max-xl:pointer-events-none': isMenuHidden,
          },
        )}
      >
        <nav
          className={classNames(
            'w-full p-4 pb-6 rounded-lg max-h-full border border-[#EFEFEF] bg-neutral-50',
            'overflow-auto max-h-[100%] xl:max-h-[calc(100vh-(8.5rem+2rem))] max-xl:transition-all', // height - (top offset + bottom space)
            {
              'max-xl:invisible max-xl:pointer-events-none max-xl:opacity-0 max-xl:translate-y-6':
                isMenuHidden,
            },
          )}
        >
          <ul className="divide-y divide-primary-100">
            {headings.map(({ title, id }) => {
              const isActive = activeHeadingId === id;
              const href = `#${id}`;

              const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
                event.preventDefault();

                scrollToHeading(id);
              };

              return (
                <li key={id}>
                  <a
                    href={href}
                    onClick={handleClick}
                    className={classNames(
                      'px-4 flex items-center justify-between py-2 font-medium',
                      {
                        'text-neutral-500': !isActive,
                        'text-secondary-700': isActive,
                      },
                    )}
                  >
                    <span className="max-w-[calc(100%-10px)] truncate">{title}</span>
                    <AngleRight className="icon" />
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        <button
          onClick={toggleMenu}
          className="icon-button xl:hidden pointer-events-auto icon-button-xl rounded-full border border-primary-100 bg-neutral-50"
        >
          <Menu className="icon-lg" />
        </button>
      </div>

      <div ref={contentElRef} className="flex-1">
        {children}
      </div>
    </div>
  );
};

export default TableOfContentLayout;
